import { VertexParameters, EdgeParameters } from "@maxgraph/core";
import { CustomProperties, Identifier } from "../../models/basic.models";

/**
 * Representa o tipo de célula.
 * Pode se referir a um Vertex ou um Edge.
 */
export enum CellKind {
    VERTEX = "VERTEX",
    EDGE   = "EDGE"
}

/**
 * Constantes readonly para definir quais propriedades devem ser omitidas dos
 * tipos VertexParameters e EdgeParameters.
 *
 * Os tipos VertexParameters e EdgeParameters possuem propriedades que precisamos
 * ignorar para criar um tipo customizado. Ver @CustomVertexParameters e @CustomEdgeParameters.
 *
 * Foi implementado desta forma porque, até o momento, o Typescript não permite
 * converter um union em um array com seus valores. Mas é possível definir um
 * array e criar um union a partir dele.
 *
 * Ver https://stackoverflow.com/a/45486495/2137778
 */
export const ALL_IGNORED_VERTEX_PROPERTIES = ["parent"] as const;
export const ALL_IGNORED_EDGE_PROPERTIES   = ["parent", "target", "source"] as const;

/**
 * Tipos usados para definir quais propriedades devem ser omitidas dos
 * tipos VertexParameters e EdgeParameters.
 *
 * É criado um Union dos valores definidos nas constantes em questão.
 *
 * Esta declaração:
 *     const ALL_IGNORED_EDGE_PROPERTIES = ["parent", "target", "source"] as const;
 *     type IgnoredEdgeProperties = typeof ALL_IGNORED_EDGE_PROPERTIES[number];
 *
 * é equivalente a:
 *     type IgnoredEdgeProperties = "parent" | "target" | "source";
 *
 * A diferença é que fica disponibilizada uma forma de iterar sobre ALL_IGNORED_EDGE_PROPERTIES.
 *
 * Ver https://stackoverflow.com/a/45486495/2137778
 */
type IgnoredVertexProperties = typeof ALL_IGNORED_VERTEX_PROPERTIES[number];
type IgnoredEdgeProperties   = typeof ALL_IGNORED_EDGE_PROPERTIES[number];

/**
 * Definição do mapeamento necessário para facilitar a conversão entre
 * CustomVertexParameters e CustomEdgeParameters para VertexParameters e EdgeParameters, respectivamente
 */
type IgnoredPropertiesMap = {
    [CellKind.VERTEX]: typeof ALL_IGNORED_VERTEX_PROPERTIES,
    [CellKind.EDGE]:   typeof ALL_IGNORED_EDGE_PROPERTIES
}

/**
 * Constante que contém o mapeamento necessário para facilitar a conversão entre
 * CustomVertexParameters e CustomEdgeParameters para VertexParameters e EdgeParameters, respectivamente
 */
export const IGNORED_PROPERTIES_MAP: IgnoredPropertiesMap = {
    [CellKind.VERTEX]: ALL_IGNORED_VERTEX_PROPERTIES,
    [CellKind.EDGE]:   ALL_IGNORED_EDGE_PROPERTIES
}

/**
 * Definição customizada para VertexParameters,
 * sobreescrevendo a propriedade `parent` de `Cell` para `string`.
 *
 * Isto é necessário porque no momento que os comandos são gerados,
 * as chamadas para de fato desenhar os elementos na tela ainda não foram executadas,
 * e como obtemos a referência para `Cell` no resultado dessas chamadas, não há `Cell`
 * criado para referenciar aqui.
 *
 * Ao usar esta interface, isso permite que as referências entre células possam
 * ser criadas a partir de seus identificadores. Posteriormente uma conversão de
 * identificador para célula será necessária.
 */
export interface CustomVertexParameters extends Omit<VertexParameters, IgnoredVertexProperties> {
    parent?: string;
    custom?: CustomProperties;
}

/**
 * Definição customizada para EdgeParameters,
 * sobreescrevendo as propriedade `parent`, `source` e `target` de `Cell` para `string`.
 *
 * Isto é necessário porque no momento que os comandos são gerados,
 * as chamadas para de fato desenhar os elementos na tela ainda não foram executadas,
 * e como obtemos a referência para `Cell` no resultado dessas chamadas, não há `Cell`
 * criado para referenciar aqui.
 *
 * Ao usar esta interface, isso permite que as referências entre células possam
 * ser criadas a partir de seus identificadores. Posteriormente uma conversão de
 * identificador para célula será necessária.
 */
export interface CustomEdgeParameters extends Omit<EdgeParameters, IgnoredEdgeProperties> {
    parent?: string;
    source?: string;
    target?: string;
    custom?: CustomProperties;
}

/**
 * Mapeamento entre os tipos customizados e os tipos de células.
 */
export type CellParameterKindMap = {
    [CellKind.VERTEX]: CustomVertexParameters,
    [CellKind.EDGE]:   CustomEdgeParameters
}

/**
 * Isto é um tipo mapeado, que resulta num union de opções específicas.
 *
 * Ele itera sobre cada membro `K` dentro da enum `CellKind`.
 *
 * Para cada `K`, ele cria uma propriedade onde `K` é a chave (e.g., `CellKind.VERTEX`)
 * e o valor é o `DrawCommand` espcífico desejado:
 *     `{ id: string; name?: string; kind: K; parameters: CellParameterKindMap[K]; }`
 *
 * Criando assim o seguinte objeto:
 *     {
 *         "VERTEX": { id: string; name?: string; kind: CellKind.VERTEX; parameters: VertexParameters; };
 *         "EDGE":   { id: string; name?: string; kind: CellKind.EDGE;   parameters: EdgeParameters;   };
 *     }
 *
 * Depois de criar esse objeto, imediatamente se acessa suas propriedades usando `[CellKind]`.
 * Como o próprio `CellKind` é essencialmente um union dos seus membro (`"VERTEX" | "EDGE"`),
 * indexar este objeto com `[CellKind]` na prática extrai os tipos dos valores correspondentes
 * as chaves e combina elas num union.
 *
 * Criando assim o seguinte union:
 *     ( { id: string; name?: string; kind: CellKind.VERTEX; parameters: VertexParameters; } )
 *   | ( { id: string; name?: string; kind: CellKind.EDGE;   parameters: EdgeParameters;   } )
 *
 * Deste modo, o tipo `AnyDrawCommand` é um union de tipos específicos de `DrawCommand`.
 *
 * Assim, não é possível construir um AnyDrawCommand com `kind: CellKind` (i.e. `AnyDrawCommand<CellKind>`)
 * porque o tipo AnyDraw é um union de tipos específicos.
 *
 * Para saber mais, ver: https://t3.chat/share/r9zg3o0uxy
 */
export type AnyDrawCommand = {
    [K in CellKind]: {
        id: Identifier;
        name?: string;
        kind: K;
        parameters: CellParameterKindMap[K];
    };
}[CellKind];
